import {
  type HolidayRegion,
  holidayRegions,
  type UserSettings,
  type WeekdayKey,
  weekdayKeys,
} from '@hugo/core'
import { useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select'
import { Skeleton } from '@/components/ui/skeleton'
import { formatHours } from '@/lib/format'
import { queryKeys, useRunTool, useSettings } from '@/lib/items'
import { PLANNING_CHOICES } from '@/lib/plan'
import { REGION_LABELS } from '@/lib/settings-sections'

const DAY_LABELS: Record<WeekdayKey, string> = {
  mon: 'Mon',
  tue: 'Tue',
  wed: 'Wed',
  thu: 'Thu',
  fri: 'Fri',
  sat: 'Sat',
  sun: 'Sun',
}

function timeZones(current: string): string[] {
  const all = typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : []
  return all.includes(current) ? all : [current, ...all]
}

function Editor({ settings }: { settings: UserSettings }) {
  const { run } = useRunTool()
  const queryClient = useQueryClient()
  const firstWorkday = weekdayKeys.find((d) => settings.workingHours[d].length > 0)
  const firstWindow = firstWorkday ? settings.workingHours[firstWorkday][0] : undefined
  const [timezone, setTimezone] = useState(settings.timezone)
  const [days, setDays] = useState(
    () => new Set(weekdayKeys.filter((d) => settings.workingHours[d].length > 0)),
  )
  const [start, setStart] = useState(firstWindow?.[0] ?? '09:00')
  const [end, setEnd] = useState(firstWindow?.[1] ?? '18:00')
  const [region, setRegion] = useState<HolidayRegion>(settings.holidayRegion)
  const [weekStart, setWeekStart] = useState<0 | 1>(settings.weekStart)
  const [planning, setPlanning] = useState(settings.planningMinutesPerDay)
  const zones = useMemo(() => timeZones(settings.timezone), [settings.timezone])
  const valid = start < end && days.size > 0

  const save = async () => {
    const workingHours = Object.fromEntries(
      weekdayKeys.map((d) => [d, days.has(d) ? [[start, end]] : []]),
    ) as UserSettings['workingHours']
    const result = await run(
      'settings.update',
      { timezone, workingHours, holidayRegion: region, weekStart, planningMinutesPerDay: planning },
      { done: 'Working time saved. Insights are being recalculated.' },
    )
    // Right away: the plan's words ("At 4 h a day …") read the hours a day from here.
    if (result) queryClient.setQueryData(queryKeys.settings, result)
  }

  return (
    <>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="timezone">Time zone</Label>
          <NativeSelect
            id="timezone"
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            className="w-full"
          >
            {zones.map((z) => (
              <NativeSelectOption key={z} value={z}>
                {z.replaceAll('_', ' ')}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </div>
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1.5 text-sm font-medium">Working days</legend>
          <div className="flex flex-wrap gap-x-4 gap-y-2">
            {weekdayKeys.map((d) => (
              <label key={d} className="flex items-center gap-2 text-sm" htmlFor={`day-${d}`}>
                <Checkbox
                  id={`day-${d}`}
                  checked={days.has(d)}
                  onCheckedChange={(checked) => {
                    const next = new Set(days)
                    if (checked) next.add(d)
                    else next.delete(d)
                    setDays(next)
                  }}
                />
                {DAY_LABELS[d]}
              </label>
            ))}
          </div>
        </fieldset>
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="work-start">Start</Label>
            <Input
              id="work-start"
              type="time"
              value={start}
              onChange={(e) => setStart(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="work-end">End</Label>
            <Input id="work-end" type="time" value={end} onChange={(e) => setEnd(e.target.value)} />
          </div>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="holidays">Public holidays</Label>
            <NativeSelect
              id="holidays"
              value={region}
              onChange={(e) => setRegion(e.target.value as HolidayRegion)}
              className="w-full"
            >
              {holidayRegions.map((r) => (
                <NativeSelectOption key={r} value={r}>
                  {REGION_LABELS[r]}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="week-start">Week starts on</Label>
            <NativeSelect
              id="week-start"
              value={String(weekStart)}
              onChange={(e) => setWeekStart(e.target.value === '0' ? 0 : 1)}
              className="w-full"
            >
              <NativeSelectOption value="1">Monday</NativeSelectOption>
              <NativeSelectOption value="0">Sunday</NativeSelectOption>
            </NativeSelect>
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="planning-hours">Hours a day for tasks and promises</Label>
          <NativeSelect
            id="planning-hours"
            value={String(planning)}
            onChange={(e) => setPlanning(Number(e.target.value))}
            aria-describedby="planning-hours-hint"
            className="w-full sm:w-48"
          >
            {PLANNING_CHOICES.map((minutes) => (
              <NativeSelectOption key={minutes} value={minutes}>
                {formatHours(minutes)}
              </NativeSelectOption>
            ))}
          </NativeSelect>
          <p id="planning-hours-hint" className="text-sm text-muted-foreground">
            hugo plans your dated tasks and promises into this much of each working day; meetings
            and everything else take the rest. With Google Calendar connected, a day full of
            meetings leaves less.
          </p>
        </div>
        {!valid ? (
          <p className="text-sm text-destructive">
            Pick at least one day and an end after the start.
          </p>
        ) : null}
      </CardContent>
      <CardFooter>
        <Button onClick={() => void save()} disabled={!valid}>
          Save working time
        </Button>
      </CardFooter>
    </>
  )
}

/**
 * The settings this card edits. The editor starts over when they change (saved here or in
 * another tab), but not when other settings change (e.g. hugo's auto-filing), which would throw
 * away unsaved edits.
 */
function workingTimeKey(settings: UserSettings): string {
  const { timezone, weekStart, workingHours, holidayRegion, planningMinutesPerDay } = settings
  return JSON.stringify([timezone, weekStart, workingHours, holidayRegion, planningMinutesPerDay])
}

/**
 * Working hours and holidays: durations in insights count working time by default, and hugo's
 * plan fills the hours a day set here with dated tasks and promises.
 */
export function WorkingTimeCard() {
  const settings = useSettings()
  return (
    <Card data-testid="working-time">
      <CardHeader>
        <CardTitle>Working time</CardTitle>
        <CardDescription>
          Insights measure durations in your working time: a request on Friday at 17:00 answered on
          Monday at 09:00 took one working hour, not 64.
        </CardDescription>
      </CardHeader>
      {settings.data ? (
        <Editor key={workingTimeKey(settings.data)} settings={settings.data} />
      ) : (
        <CardContent>
          <Skeleton className="h-40 w-full" />
        </CardContent>
      )}
    </Card>
  )
}
